import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  PageHeader,
  DashboardGrid,
  DashboardCard,
  StatusBadge,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@zafroi/ui-kit';
import { useAccount, useKoshLabels } from '@/hooks/useKosh';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { accountStatusVariants } from '@/config/kosh-status';

export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const q = useAccount(id);
  const { label } = useKoshLabels();

  return (
    <div className="space-y-6">
      <QueryStateGuard data={q.data} isLoading={q.isLoading} isError={q.isError}>
        {(a) => (
          <>
            <PageHeader
              eyebrow={
                <Link to="/customers" className="inline-flex items-center gap-1 hover:text-foreground">
                  <ArrowLeft className="h-3.5 w-3.5" /> {label('customer')}s
                </Link>
              }
              title={a.name}
              meta={<StatusBadge status={a.status} variants={accountStatusVariants} />}
              description={`${a.code}${a.industry ? ` · ${a.industry}` : ''}`}
            />

            <DashboardGrid columns={2}>
              <DashboardCard title="Linked customers">
                {a.customers && a.customers.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {a.customers.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="font-mono text-[12px]">{c.code}</TableCell>
                          <TableCell className="font-medium">{c.name}</TableCell>
                          <TableCell className="text-muted-foreground">{c.email ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground">No linked customers.</p>
                )}
              </DashboardCard>

              <DashboardCard title="Contacts">
                {a.contacts && a.contacts.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Email</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {a.contacts.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="font-medium">
                            {c.name}
                            {c.isPrimary && (
                              <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                                Primary
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground">{c.title ?? '—'}</TableCell>
                          <TableCell className="text-muted-foreground">{c.email ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground">No contacts.</p>
                )}
              </DashboardCard>
            </DashboardGrid>
          </>
        )}
      </QueryStateGuard>
    </div>
  );
}
